import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import JourneyStepper from '../components/JourneyStepper';
import { ShippingAddressForm } from './CustomerInfo/ShippingAddressForm';
import { OrderCostSummary } from './CustomerInfo/OrderCostSummary';
import { ShippingRateSelector } from '../components/ShippingRateSelector';
import { useShippingRates } from '../hooks/useShippingRates';
import { getStatesForCountry } from '../lib/geoData';
import { taxBreakdown } from '../lib/taxCalculator';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerInfoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    customer_po_number: '',
    first_name: '',
    last_name: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    city: '',
    country: 'United States',
    state_province: '',
    postal_code: '',
    email: ''
  });

  const [poFile, setPoFile] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const shippingRates = useShippingRates();

  // Trigger a quick Freightcom estimate as soon as the shipping address is
  // populated enough to resolve. Debounced by waiting for postal code + city.
  useEffect(() => {
    if (!order?.items?.length) return;
    if (!formData.postal_code || !formData.city || !formData.country) return;
    const province = formData.state_province;
    if (!province) return;
    const country = formData.country?.toLowerCase().includes('canad') ? 'CA'
                  : formData.country?.toLowerCase().includes('states') ? 'US'
                  : 'CA';
    const handle = setTimeout(() => {
      shippingRates.fetchRates({
        shipTo: {
          street: formData.address_line1 || 'Address TBD',
          city: formData.city,
          province,
          postal_code: formData.postal_code,
          country,
          residential: true,
        },
        items: order.items,
      }).catch(() => {/* error already captured in hook */});
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.postal_code, formData.city, formData.state_province, formData.country, formData.address_line1, order?.id]);

  // Define fetchOrder BEFORE the useEffect that uses it
  const fetchOrder = useCallback(async (id) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/enhanced-orders/${id}`
        // Cookie automatically sent with request
      );
      setOrder(response.data);

      // Pre-fill if shipping info exists
      if (response.data.shipping_info) {
        setFormData(prev => ({
          ...prev,
          ...response.data.shipping_info
        }));
      }
    } catch (error) {
      log.error('Error fetching order:', error);
      // Silently fail - user can still proceed with form
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (location.state?.orderId) {
      setOrderId(location.state.orderId);
      fetchOrder(location.state.orderId);

      // Debug: Log what we received from QuoteCalculator
      log.info('CustomerInfoPage - Received location.state:', {
        hasShippingAddress: !!location.state?.shippingAddress,
        country: location.state?.country,
        fullState: location.state
      });

      // Combine all form data updates into a single operation
      setFormData(prev => {
        const updates = { ...prev };

        // Pre-fill with shipping address from QuoteCalculator if provided
        if (location.state?.shippingAddress) {
          const shipAddr = location.state.shippingAddress;
          updates.company_name = shipAddr.companyName || updates.company_name;
          updates.address_line1 = shipAddr.addressLine1 || '';
          updates.address_line2 = shipAddr.addressLine2 || '';
          updates.city = shipAddr.city || '';
          updates.state_province = shipAddr.stateProvince || '';
          updates.postal_code = shipAddr.postalCode || '';
          updates.country = location.state?.country || updates.country; // Preserve country from quote stage

          log.info('CustomerInfoPage - Setting country to:', updates.country);
        }

        // Pre-fill with user data if available. The Account Settings page
        // now persists shipping / contact fields, so we surface them here
        // so repeat customers don't retype their address on every order.
        // Shipping-info already saved on the order wins over profile
        // defaults (handled by the earlier spread in fetchOrder), so this
        // only fills gaps.
        if (user) {
          updates.first_name = user.first_name || updates.first_name;
          updates.last_name = user.last_name || updates.last_name;
          updates.company_name = updates.company_name || user.company || '';
          updates.email = user.email || '';
          updates.phone = updates.phone || user.phone || '';
          updates.address_line1 = updates.address_line1 || user.address_line1 || '';
          updates.address_line2 = updates.address_line2 || user.address_line2 || '';
          updates.city = updates.city || user.city || '';
          updates.postal_code = updates.postal_code || user.postal_code || '';
          // state_region (profile) maps to state_province (checkout).
          updates.state_province =
            updates.state_province || user.state_region || '';
          // Respect whatever country is already in updates (from the
          // quote stage) but fall back to the profile before the default
          // "United States of America" baked into formData.
          if (!location.state?.country && user.country) {
            updates.country = user.country;
          }
        }

        return updates;
      });
    } else {
      navigate('/quote');
    }
  }, [isAuthenticated, location, navigate, user, fetchOrder]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF only for PO)
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file for the Purchase Order');
        e.target.value = '';
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      setPoFile(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      // iter114s.39 — when the country changes, drop the previously
      // selected state/province if it's not valid for the new country.
      // Without this, a Canadian "Ontario" pick would persist when the
      // customer flips Country to "United States", silently shipping
      // to an invalid US address. `getStatesForCountry` returns null
      // for free-text countries (everything outside US/Canada) — in
      // that case we also clear so the customer re-types something
      // appropriate to the new country.
      if (name === 'country' && value !== prev.country) {
        const newStates = getStatesForCountry(value);
        const stillValid = newStates ? newStates.includes(prev.state_province) : false;
        return {
          ...prev,
          country: value,
          state_province: stillValid ? prev.state_province : '',
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const requiredFields = ['first_name', 'last_name', 'company_name', 'address_line1', 'phone', 'city', 'country', 'state_province', 'postal_code', 'email'];
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());

    if (missingFields.length > 0) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Map frontend fields to backend expected fields
      const shippingData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_name: formData.company_name,
        contact_name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone: formData.phone,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || "",
        city: formData.city,
        state_province: formData.state_province,
        postal_code: formData.postal_code,
        country: formData.country,
        purchase_order_number: formData.customer_po_number || "",
        shipping_instructions: ""
      };

      await axios.put(
        `${API_URL}/api/enhanced-orders/${orderId}/shipping-info`,
        shippingData
        // Cookie automatically sent with request
      );

      // Upload PO file if provided
      if (poFile) {
        const formData = new FormData();
        formData.append('order_id', orderId);
        formData.append('file', poFile);

        await axios.post(
          `${API_URL}/api/files/upload-po`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
            // Cookie automatically sent with request
          }
        );
      }

      // Path-param URL so bookmarks + refreshes keep order context.
      navigate(`/review-order/${orderId}`, {
        state: {
          orderId,
          selectedShippingRate: selectedRate,
          shippingPackage: shippingRates.package,
          shippingShipFrom: shippingRates.shipFrom,
        },
      });
    } catch (error) {
      alert('Failed to save shipping information. Please try again.');
    }
  };

  // Match the ReviewOrder page's currency resolution: prefer the order's
  // stored currency, fall back to the user's country. Previously hard-
  // coded to USD which was wrong for Canadian customers.
  const activeCurrency = (() => {
    const c = (order?.currency || '').toUpperCase();
    if (c === 'CAD' || c === 'USD') return c;
    return user?.country === 'Canada' ? 'CAD' : 'USD';
  })();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(activeCurrency === 'CAD' ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: activeCurrency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const subtotal = order?.total_price || 0;
  const shipping = selectedRate?.total_price ?? 4.99;
  // iter114s.60 — Tax is now LIVE on the Customer Info page. The
  // postal-code auto-detect from iter114s.59 auto-fills the
  // state_province dropdown the moment the customer types their
  // postal/ZIP code; `taxBreakdown` re-runs and the cost summary
  // updates the tax line + the running total in the same paint.
  // Mirrors the backend `tax_breakdown` so the customer sees the
  // exact final number on the very next page (Review Order).
  const { rate: taxRate, label: taxLabel, taxAmount: tax } = taxBreakdown(
    subtotal,
    shipping,
    formData.country,
    formData.state_province,
  );
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* iter114s.45 — Per-page brand band removed. */}
      <JourneyStepper currentStage={4} orderId={orderId} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 text-center">Customer Info</h1>
        <div className="bg-blue-50 border-l-4 border-[#4A7C59] p-4 mb-6 max-w-4xl mx-auto">
          <p className="text-sm text-gray-700">
            <strong className="text-[#4A7C59]">📦 Shipping Information</strong><br />
            Please enter your complete shipping address. This is where your order will be delivered.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Form Fields */}
            <div className="lg:col-span-2">
              <ShippingAddressForm
                formData={formData}
                onChange={handleChange}
                onFileChange={handleFileChange}
                poFile={poFile}
                onAddItem={() => navigate('/quote', { state: { orderId, addToExisting: true } })}
              />

              {(shippingRates.loading || shippingRates.error || shippingRates.rates.length > 0) && (
                <div className="bg-white rounded-lg shadow-lg p-6 mt-6" data-testid="live-shipping-card">
                  <h3 className="text-lg font-bold text-[#4A7C59] mb-1 flex items-center gap-2">
                    Live Shipping Rates
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Top 3 cheapest carriers from Freightcom. Final price locks in on the Review page.
                  </p>
                  <ShippingRateSelector
                    rates={shippingRates.rates}
                    loading={shippingRates.loading}
                    error={shippingRates.error}
                    mockMode={shippingRates.mockMode}
                    pkg={shippingRates.package}
                    totalCarriers={shippingRates.totalCarriers}
                    selectedServiceCode={selectedRate?.service_code}
                    onSelect={setSelectedRate}
                  />
                </div>
              )}
            </div>

            {/* Right: Cost Summary */}
            <div className="lg:col-span-1">
              <OrderCostSummary
                itemCount={order?.items?.length || 0}
                subtotal={subtotal}
                shipping={shipping}
                tax={tax}
                taxLabel={taxLabel}
                total={total}
                currency={activeCurrency}
                formatCurrency={formatCurrency}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerInfoPage;
